import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Row, 
  Col, 
  Spin, 
  Typography, 
  List, 
  message, 
  Tag, 
  Divider, 
  Button, 
  Modal,
  Descriptions,
  Image
} from 'antd';
import { 
  LoadingOutlined, 
  ShoppingCartOutlined, 
  HomeOutlined, 
  ClockCircleOutlined, 
  EnvironmentOutlined,
  CheckCircleOutlined,
  TruckOutlined,
  DollarOutlined,
  ShoppingOutlined,
  FilePdfOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const OrderHistory = () => {
  const hosturl = 'https://shopbackend.raoosastro.com';
  const [orders, setOrders] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserDetails();
  }, []);

  const fetchUserDetails = async () => {
    if (!token) {
      setError('Please log in to view your profile.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`https://api.raoosastro.com/user-details`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.data.user) {
        setError('User not found. Please log in.');
        return;
      }

      setUser(response.data.user);
      fetchOrders(response.data.user._id);
    } catch (err) {
      setError('An unexpected error occurred. Please login again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async (userId) => {
    
    try {
      const res = await axios.get(`${hosturl}/shop-userorders/${userId}`);
      setOrders(res.data?.reverse());
    } catch (error) {
      console.error("Error fetching orders:", error);
      setError('Error fetching orders. Please try again later.');
    }
  };

  const getStatusTag = (status) => {
    let color, icon;
    switch (status.toLowerCase()) {
      case 'completed':
        color = 'green';
        icon = <CheckCircleOutlined />;
        break;
      case 'pending':
        color = 'orange';
        icon = <ClockCircleOutlined />;
        break;
      case 'dispatched':
        color = 'blue';
        icon = <TruckOutlined />;
        break;
      case 'paid':
        color = 'green';
        icon = <CheckCircleOutlined />;
        break;
      default:
        color = 'gray';
        icon = <ShoppingCartOutlined />;
    }
    return <Tag icon={icon} color={color} style={{ borderRadius: '12px' }}>{status}</Tag>;
  };

  const showOrderDetails = (order) => {
    setSelectedOrder(order);
    setModalVisible(true);
  };

  const handleCancel = () => {
    setModalVisible(false);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh' 
      }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 36 }} spin />} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '40px',
        background: '#fff',
        borderRadius: '8px',
        maxWidth: '600px',
        margin: '20px auto',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <Title level={4} style={{ color: '#ff4d4f' }}>
          {error}
        </Title>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '24px',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        marginBottom: '24px',
        padding: '16px',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        borderRadius: '8px'
      }}>
        <Title level={3} style={{ 
          margin: 0,
          color: '#2c3e50',
          fontWeight: 600
        }}>
          Shop Orders
        </Title>
      </div>

      {orders.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          background: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <img 
            src="https://cdn-icons-png.flaticon.com/512/4076/4076478.png" 
            alt="No orders" 
            style={{ 
              width: '120px', 
              opacity: 0.7,
              marginBottom: '16px'
            }} 
          />
          <Title level={4} style={{ color: '#7f8c8d' }}>
            You haven't placed any orders yet
          </Title>
          <Text type="secondary">
            Start shopping to see your orders here
          </Text>
        </div>
      ) : (
        <Row gutter={[24, 24]}>
          {orders.map((order) => (
            <Col key={order._id} xs={24} sm={24} lg={12} xl={8}>
              <Card
                hoverable
                headStyle={{
                  background: '#f8f9fa',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px'
                }}
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text strong style={{ color: '#2c3e50' }}>
                      Order #{order._id.substring(0, 8).toUpperCase()}
                    </Text>
                    {getStatusTag(order.status)}
                  </div>
                }
                style={{
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  transition: 'transform 0.3s',
                  height: '100%'
                }}
                bodyStyle={{ padding: '16px' }}
              
              >
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <Text strong>
                      <ClockCircleOutlined style={{ marginRight: '8px', color: '#7f8c8d' }} />
                      Order Date
                    </Text>
                    <Text>{new Date(order.createdAt).toLocaleDateString()}</Text>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <Text strong>
                      <DollarOutlined style={{ marginRight: '8px', color: '#7f8c8d' }} />
                      Total Amount
                    </Text>
                    <Text strong style={{ color: '#e67e22', fontSize: '16px' }}>
                      ₹{order.totalAmount.toFixed(2)}
                    </Text>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}>
                    <Text strong>
                      <EnvironmentOutlined style={{ marginRight: '8px', color: '#7f8c8d' }} />
                      Delivery Address
                    </Text>
                    <Text style={{ textAlign: 'right', maxWidth: '60%' }}>
                      {order.address[0]?.houseNo}, {order.address[0]?.area}, {order.address[0]?.city}
                    </Text>
                  </div>
                </div>
                
                <Divider style={{ margin: '12px 0' }} />
                
                <Title level={5} style={{ marginBottom: '12px' }}>
                  Ordered Items
                </Title>
                
                <List
                  size="small"
                  dataSource={order?.products}
                  renderItem={(product) => {
                    const productData = product.productId;
                    return (
                      <List.Item 
                        style={{ 
                          padding: '8px 0',
                          borderBottom: '1px solid #f0f0f0'
                        }}
                      >
                        <div style={{ display: 'flex', width: '100%' }}>
                          {productData?.product_images?.[0] ? (
                            <img
                              src={`${hosturl}${productData?.product_images?.[0]}`}
                              alt={productData?.product_name}
                              style={{
                                width: '60px',
                                height: '60px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                marginRight: '12px'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '60px',
                              height: '60px',
                              background: '#f0f0f0',
                              borderRadius: '4px',
                              marginRight: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <ShoppingCartOutlined style={{ fontSize: '20px', color: '#999' }} />
                            </div>
                          )}
                          <div style={{ flex: 1 }}>
                            <Text strong style={{ display: 'block' }}>
                              {productData?.product_name || 'Product not available'}
                            </Text>
                            <Text type="secondary">
                              Qty: {product.quantity}
                            </Text>
                          </div>
                          <Text strong style={{ alignSelf: 'center' }}>
                            ₹{product.price}
                          </Text>
                        </div>
                      </List.Item>
                    );
                  }}
                />
                 { (order.courierNumber || order.slipFile) && (
                    <Button 
                      type="link" 
                      style={{float:'right'}}
                      icon={<InfoCircleOutlined />}
                      onClick={() => showOrderDetails(order)}
                    >
                      Details
                    </Button>
                  )}
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Order Details Modal */}
      <Modal
        title={`Order Details - #${selectedOrder?._id.substring(0, 8).toUpperCase()}`}
        visible={modalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedOrder && (
          <div>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Order ID">{selectedOrder.orderId}</Descriptions.Item>
              <Descriptions.Item label="Status">
                {getStatusTag(selectedOrder.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Order Date">
                {new Date(selectedOrder.createdAt).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Payment Method">
                {selectedOrder.paymentMethod}
              </Descriptions.Item>
              <Descriptions.Item label="Payment Status">
                {getStatusTag(selectedOrder.paymentStatus)}
              </Descriptions.Item>
              <Descriptions.Item label="Total Amount">
                ₹{selectedOrder.totalAmount.toFixed(2)}
              </Descriptions.Item>
              {selectedOrder.CGST && (
                <Descriptions.Item label="CGST">
                  ₹{selectedOrder.CGST.toFixed(2)}
                </Descriptions.Item>
              )}
              {selectedOrder.SGST && (
                <Descriptions.Item label="SGST">
                  ₹{selectedOrder.SGST.toFixed(2)}
                </Descriptions.Item>
              )}
              {selectedOrder.courierNumber && (
                <Descriptions.Item label="Courier Number">
                  {selectedOrder.courierNumber}
                </Descriptions.Item>
              )}
              {selectedOrder.slipFile && (
                <Descriptions.Item label="Payment Slip">
                  <a 
                    href={`${hosturl}${selectedOrder.slipFile}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <FilePdfOutlined style={{ marginRight: '8px', color: 'red' }} />
                    View Payment Slip
                  </a>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider orientation="left">Delivery Address</Divider>
            <div style={{ padding: '0 16px' }}>
              <Text strong>{selectedOrder.address[0]?.fullName}</Text><br />
              <Text>{selectedOrder.address[0]?.houseNo}, {selectedOrder.address[0]?.buildingName}</Text><br />
              <Text>{selectedOrder.address[0]?.area}, {selectedOrder.address[0]?.city}</Text><br />
              <Text>{selectedOrder.address[0]?.state} - {selectedOrder.address[0]?.pincode}</Text><br />
              <Text>Phone: {selectedOrder.address[0]?.phoneNumber}</Text>
            </div>

            <Divider orientation="left">Ordered Items</Divider>
            <List
              dataSource={selectedOrder.products}
              renderItem={(product) => (
                <List.Item>
                  <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <Text strong>{product.productId?.product_name || 'Product not available'}</Text>
                    </div>
                    <div style={{ width: '100px', textAlign: 'right' }}>
                      <Text>Qty: {product.quantity}</Text>
                    </div>
                    <div style={{ width: '100px', textAlign: 'right' }}>
                      <Text strong>₹{product.price}</Text>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrderHistory;